/**
 * Dynamically determines the API URL based on the runtime environment.
 * In the browser, it uses the current origin.
 * On the server (during build/SSR), it falls back to the environment variable.
 */
export const getApiUrl = () => {
  if (typeof window !== "undefined") {
    // browser side - always use the current domain to avoid CORS and domain mismatch
    return `${window.location.origin}/api`;
  }

  // server side (build time or SSR)
  const rawApiUrl =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";
  return rawApiUrl.endsWith("/api") ? rawApiUrl : `${rawApiUrl}/api`;
};

export const API_URL = getApiUrl();
export const API_BASE = API_URL.replace(/\/api$/, "");

/**
 * Dynamically determines the WebSocket URL.
 * In production, uses the current origin (proxied via Next.js rewrites).
 * In local dev, connects directly to the backend port to avoid Next.js rewrite issues with WebSockets.
 */
export const getSocketUrl = () => {
  if (typeof window !== "undefined") {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocalhost) {
      // Direct connection to backend in dev - Next.js rewrites are unreliable for WebSockets
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";
      return apiUrl.replace(/\/api\/?$/, "");
    }
    return window.location.origin;
  }
  const rawApiUrl =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";
  return rawApiUrl.replace(/\/api\/?$/, "");
};

export const SOCKET_URL = getSocketUrl();

export const getBackendAssetUrl = (url?: string | null) => {
  if (!url) return "";
  if (
    url.startsWith("http") ||
    url.startsWith("blob:") ||
    url.startsWith("data:")
  )
    return url;
  const requestedPath = url.startsWith("/") ? url : `/${url}`;
  // Older catalog imports used the BR variant as the universal Mobile
  // Legends artwork even though only the shared MY artwork is bundled.
  const assetAliases: Record<string, string> = {
    "/images/games/mlbb-br.webp": "/images/games/mlbb-my.webp",
  };
  const cleanPath = assetAliases[requestedPath] || requestedPath;
  // Catalog artwork is bundled with the storefront and stays on its origin.
  if (cleanPath.startsWith("/images/")) return cleanPath;
  const rawApiUrl =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";
  const backendOrigin = rawApiUrl.replace(/\/api\/?$/, "");

  if (typeof window !== "undefined") {
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      return `${backendOrigin}${cleanPath}`;
    }
    return cleanPath;
  }

  return cleanPath.startsWith("/uploads/")
    ? `${backendOrigin}${cleanPath}`
    : cleanPath;
};
